import { AssetGroup } from '@play-co/timestep-core';
import {
  ButtonView,
  ImageScaleView,
  ImageSliceView,
  View,
  animate,
} from '@play-co/timestep-core/ui';
import { downscale } from 'src/conf/scale';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import DreamDriftTitle from 'src/game/dreamDrift/components/DreamDriftTitle';
import i18n from 'src/lib/i18n/i18n';
import sounds from 'src/lib/sounds';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { getScreenDimensions, waitForItPromise } from 'src/lib/utils';
import { Feature } from 'src/replicant/analytics';
import {
  dreamDriftGetFinalNode,
  dreamDriftGetCheckpoints,
} from 'src/replicant/dreamDrift/dreamDrift.getters';
import StateObserver from 'src/StateObserver';

/** Native width of the supplied Dream Drift information design. */
const DESIGN_WIDTH = 720;

/** Native height of the supplied Dream Drift information design. */
const DESIGN_HEIGHT = 1280;

/** Close callback invoked when the player taps the information popup. */
type PopupDreamDriftInfoClose = () => void;

/** Fullscreen Dream Drift introduction explaining the event rules. */
export default class PopupDreamDriftInfo extends PopupBasicLazy<PopupDreamDriftInfoClose> {
  /** Native-resolution container scaled to fit the current viewport. */
  private readonly content: View;

  /**
   * Create the fullscreen overlay and its fixed native-resolution composition.
   *
   * @param opts Popup parent and close callback supplied by the popup registry.
   */
  constructor(opts: { superview: View; close: PopupDreamDriftInfoClose }) {
    // Size the borderless popup to the complete current viewport.
    const width = opts.superview.style.width;
    const { height } = getScreenDimensions();

    // Create the fullscreen popup with dedicated Dream Drift tracking.
    super({
      superview: opts.superview,
      close: opts.close,
      width,
      height,
      skipBackground: true,
      skipTitle: true,
      skipMessage: true,
      tracking: {
        feature: Feature.DREAM_DRIFT._,
        $subFeature: Feature.DREAM_DRIFT.POPUP_INFO,
      },
      zIndex: 10001,
    });

    // Use the same dark overlay treatment as the existing FTUE info popups.
    this.bg.updateOpts({
      backgroundColor: 'rgba(71, 24, 67, 0.9)',
    });

    // The information popup closes only when the player taps the screen.
    this.buttonClose.removeFromSuperview();

    // Scale the 720-by-1280 composition to shorter or narrower screens.
    const contentScale = Math.min(width / DESIGN_WIDTH, height / DESIGN_HEIGHT);
    this.content = new View({
      superview: this.box,
      width: DESIGN_WIDTH,
      height: DESIGN_HEIGHT,
      x: width / 2,
      y: height / 2,
      scale: contentScale,
      centerOnOrigin: true,
      canHandleEvents: false,
    });

    // Let a tap anywhere on the popup acknowledge and close the instructions.
    new ButtonView({
      superview: this.box,
      width,
      height,
      zIndex: 1,
      onClick: () => {
        // Confirm the dismissal with the standard popup sound.
        sounds.sfx.play('ok');

        // Resolve the popup after the player acknowledges the instructions.
        opts.close();
      },
    });
  }

  /**
   * Load every shared and popup-specific Dream Drift image before display.
   *
   * @returns Asset group containing the title and information artwork.
   */
  public getAssetsGroup(): AssetGroup {
    // Load the localized title and complete information asset directory.
    return new AssetGroup([
      ...AssetGroup.constructURLs([
        'assets/ui/dreamDrift/common',
        'assets/ui/dreamDrift/popupInfo',
      ]),
    ]);
  }

  /** Initialize and begin displaying the information popup. */
  public init(): void {
    // Start the shared popup fade-in lifecycle with no open arguments.
    super.init({});
  }

  /**
   * Fade in the popup chrome, then present each instruction in sequence.
   *
   * @returns Promise resolved after the instruction animation starts.
   */
  public async fadeIn(): Promise<void> {
    // Load and reveal the shared fullscreen popup container first.
    await super.fadeIn();

    // Present the Dream Drift instructions after the popup is visible.
    await this.animateIn();
  }

  /**
   * Build and animate the localized Dream Drift instruction composition.
   *
   * @returns Promise resolved after every section has been introduced.
   */
  private async animateIn(): Promise<void> {
    // Pause briefly so the fullscreen background settles before the content.
    await waitForItPromise(300);

    // Celebrate the information presentation.
    sounds.sfx.play('win');

    // Select localized artwork and typography for the current player locale.
    const locale = StateObserver.getLocale();

    // Present the shared localized event title.
    const title = new DreamDriftTitle({
      superview: this.content,
      x: DESIGN_WIDTH / 2,
      y: 100,
      centerOnOrigin: true,
    });
    await this.showAnimated(title);

    // Present the localized How to Play heading below the event title.
    const howToPlay = new ImageScaleView({
      superview: this.content,
      image: `assets/ui/dreamDrift/popupInfo/how_to_play_${locale}.png`,
      width: downscale(locale === 'en' ? 288 : 136),
      height: downscale(locale === 'en' ? 61 : 58),
      x: DESIGN_WIDTH / 2,
      y: 170,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      canHandleEvents: false,
    });
    await this.showAnimated(howToPlay);

    // Show the selected path length in the localized rules below.
    const finalNode = dreamDriftGetFinalNode();

    // Preserve the map asset's source aspect ratio at the native game scale.
    const mapWidth = downscale(762);
    const mapHeight = downscale(563);

    // Group the progression map and localized start marker into one animation.
    const map = new View({
      superview: this.content,
      width: mapWidth,
      height: mapHeight,
      x: DESIGN_WIDTH / 2,
      y: 400,
      centerOnOrigin: true,
      canHandleEvents: false,
    });

    // Show all levels, reward chest, and checkpoint flags.
    new ImageScaleView({
      superview: map,
      image: 'assets/ui/dreamDrift/popupInfo/map12.png',
      width: mapWidth,
      height: mapHeight,
      scaleMethod: 'contain',
      canHandleEvents: false,
    });

    // Render the localized START label over its marker.
    new LangBitmapFontTextView({
      superview: map,
      x: -25,
      y: 248,
      width: 150,
      height: 50,
      align: 'center',
      verticalAlign: 'center',
      color: '#FFFFFF',
      font: 'Body',
      size: 20,
      localeText: () => i18n('dreamDrift.popupInfo.start'),
    });
    await this.showAnimated(map);

    const [firstCheckpoint, secondCheckpoint] = dreamDriftGetCheckpoints();

    // Describe each event rule with its matching supplied icon and copy.
    const instructions = [
      {
        icon: 'icon_item_1.png',
        iconWidth: downscale(129),
        iconHeight: downscale(131),
        text: 'dreamDrift.popupInfo.chooseHero',
      },
      {
        icon: 'icon_item_2.png',
        iconWidth: downscale(117),
        iconHeight: downscale(90),
        text: 'dreamDrift.popupInfo.completeLevels',
      },
      {
        icon: 'icon_item_3.png',
        iconWidth: downscale(101),
        iconHeight: downscale(114),
        text: 'dreamDrift.popupInfo.checkpoints',
      },
    ] as const;

    // Build the three matching instruction panels from the shared layout.
    for (const [index, instruction] of instructions.entries()) {
      // Group each background, icon, and text into one animated section.
      const panel = new View({
        superview: this.content,
        x: DESIGN_WIDTH / 2,
        y: 700 + 160 * index,
        width: 630,
        height: 145,
        canHandleEvents: false,
        centerOnOrigin: true,
      });

      // Resize the supplied rounded panel without distorting its corners.
      new ImageSliceView({
        superview: panel,
        image: 'assets/ui/dreamDrift/popupInfo/item_box.png',
        width: panel.style.width,
        height: panel.style.height,
        slices: {
          top: 50,
          bot: 50,
          left: 50,
          right: 50,
        },
        canHandleEvents: false,
      });

      // Place the rule-specific icon at the left of the panel.
      new ImageScaleView({
        superview: panel,
        image: `assets/ui/dreamDrift/popupInfo/${instruction.icon}`,
        width: instruction.iconWidth,
        height: instruction.iconHeight,
        x: 70,
        y: panel.style.height / 2,
        centerOnOrigin: true,
        scaleMethod: 'contain',
        canHandleEvents: false,
      });

      // Render the localized rule copy with the design's green emphasis.
      new LangBitmapFontTextView({
        superview: panel,
        x: 136,
        width: 440,
        height: panel.style.height,
        align: 'left',
        verticalAlign: 'center',
        color: '#FFFFFF',
        font: 'Body',
        size: 24,
        wordWrap: true,
        isRichText: true,
        localeText: () =>
          i18n(instruction.text, {
            count: finalNode,
            firstCheckpoint,
            secondCheckpoint,
          }),
      });

      // Introduce the complete instruction panel as a single unit.
      await this.showAnimated(panel);
    }

    // Finish with the localized full-screen dismissal prompt.
    const tapToContinue = new ImageScaleView({
      superview: this.content,
      image: `assets/ui/dreamDrift/common/tap_to_continue_${locale}.png`,
      width: downscale(locale === 'en' ? 569 : 433),
      height: downscale(locale === 'en' ? 87 : 84),
      x: DESIGN_WIDTH / 2,
      y: 1170,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      canHandleEvents: false,
    });
    await this.showAnimated(tapToContinue);
  }

  /**
   * Pop one instruction section into view and play its reveal sound.
   *
   * @param view Section that should be introduced to the player.
   * @returns Promise resolved after the stagger delay completes.
   */
  private async showAnimated(view: View): Promise<void> {
    // Start the section collapsed before playing the reveal animation.
    view.updateOpts({ scale: 0 });

    // Expand the section with the same bounce used by other FTUE popups.
    animate(view).then({ scale: 1 }, 300, animate.easeOutBack);

    // Pair every visual reveal with the shared lightweight tap sound.
    sounds.sfx.play('puzzle-ice-tap');

    // Stagger the next section so the instructions read in order.
    await waitForItPromise(200);
  }
}
